<template>
  <div>
    <b-modal
      id="myWhatsappNumber"
      modal-class="modal-cover"
      v-model="isVisible"
      hide-backdrop
      content-class="shadow-lg my-whatsapp-card"
      centered
      size="lg"
      hide-header
      ok-only
      :ok-title="t('close')"
      :dir="isHebrew ? 'rtl' : 'ltr'"
    >
      <!-- ACCOUNT level, never a ride. Everything below talks to the
           self-scoped /api/whatsapp/account_link, so it answers about the
           CALLER and only about him -- which is exactly why this lives in a
           global popup off the account menu rather than on one ride's page.
           It therefore cannot say anything about who a given ride is billed
           to; the composer's own 403 not_the_ride_manager covers that. -->
      <div class="my-whatsapp-number" :dir="isHebrew ? 'rtl' : 'ltr'">
        <header class="mwn-head">
          <span class="mwn-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
              <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18a8 8 0 0 1-4.1-1.1l-.3-.2-3 .8.8-2.9-.2-.3A8 8 0 1 1 12 20zm4.4-5.8c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.6 6.6 0 0 1-3.2-2.8c-.1-.2 0-.4.1-.5l.4-.5.2-.4v-.4l-.7-1.7c-.2-.4-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4 7.6 7.6 0 0 0 3 .6 2.6 2.6 0 0 0 1.7-1.2 2.1 2.1 0 0 0 .1-1.2z"/>
            </svg>
          </span>
          <h3 class="mwn-title">{{ t('managerAlertTitle') }}</h3>
        </header>

        <p class="mwn-explain">{{ t('managerAlertExplain') }}</p>

        <!-- Not linked, or linked and deliberately replacing the number:
             both run the same mint -> send -> re-check flow, because that
             IS how a number is changed -- whoever sends the fresh code owns
             the account from then on. -->
        <template v-if="!myLink.linked || changingNumber">
          <template v-if="!myLink.link_url">
            <div class="mwn-action">
              <b-button
                variant="success"
                size="lg"
                class="mwn-primary"
                :disabled="minting"
                @click="mintAccountLink"
              >{{ t('managerAlertLinkButton') }}</b-button>
              <p class="mwn-sub">{{ t('managerAlertLinkExplain') }}</p>
            </div>
          </template>

          <template v-else>
            <div class="mwn-action">
              <b-button
                variant="success"
                size="lg"
                class="mwn-primary"
                :href="myLink.link_url"
                target="_blank"
                rel="noopener"
              >{{ t('managerAlertOpenWhatsapp') }}</b-button>
            </div>
            <!-- The code in text as well as in the link: the pre-filled
                 message lands in his compose box where he can edit it, and a
                 manager who trims it needs the code to type. -->
            <!-- The MANUAL path, for a manager whose tap did not open
                 WhatsApp, who is at a desktop without it, or who trimmed the
                 pre-filled message. Same code, same destination: the number
                 shown here is read back out of link_url rather than
                 configured separately, so the two can never drift apart and
                 send him somewhere the code does not work. -->
            <div class="mwn-manual">
              <div class="mwn-or"><span>{{ t('managerAlertManualTitle') }}</span></div>

              <p class="mwn-sub">{{ t('managerAlertManualExplain') }}</p>

              <div class="manager-alert-code mwn-code">
                <span class="mwn-code-label">{{ t('managerAlertCodeLabel') }}</span>
                <!-- Latin letters and digits: they must not mirror inside a
                     Hebrew page. -->
                <code dir="ltr">{{ myLink.link_code }}</code>
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  class="mwn-copy"
                  @click="copyLinkCode"
                >{{ t('managerAlertCopy') }}</b-button>
              </div>

              <p v-if="systemWhatsappNumber" class="mwn-sub mwn-system-number">
                <span>{{ t('managerAlertSystemNumber') }}</span>
                <code dir="ltr">{{ systemWhatsappNumber }}</code>
              </p>
            </div>
            <b-button variant="link" class="mwn-quiet" :disabled="checking"
                      @click="recheckAccountLink">
              {{ t('managerAlertRecheck') }}
            </b-button>
          </template>

          <!-- Only while REPLACING a working number: the old link is still
               the live one until the new code is sent, so backing out has to
               be possible and has to leave it alone. -->
          <b-button
            v-if="changingNumber"
            variant="link"
            size="sm"
            @click="cancelChangeNumber"
          >{{ t('cancel') }}</b-button>
        </template>

        <div v-else class="mwn-status">
          <div class="mwn-status-row">
            <span class="mwn-tick" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                   stroke="currentColor" stroke-width="3"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 12.5l5 5L20 6.5"/>
              </svg>
            </span>
            <div class="mwn-status-text">
              <span class="mwn-status-label">{{ t('managerAlertLinkedAs') }}</span>
              <code class="mwn-phone" dir="ltr">{{ myLink.phone_masked }}</code>
            </div>
          </div>
          <div v-if="!confirmingUnlink" class="mwn-status-actions">
            <b-button
              variant="outline-success"
              size="sm"
              :disabled="minting"
              @click="startChangeNumber"
            >{{ t('managerAlertChangeNumber') }}</b-button>
            <b-button
              variant="link"
              size="sm"
              class="mwn-danger-link"
              @click="confirmingUnlink = true"
            >{{ t('managerAlertUnlink') }}</b-button>
          </div>
        </div>

        <!-- Unlink is immediate and ACCOUNT-WIDE: it deactivates the link for
             every ride this account is billed for and persists even if the
             popup is then closed, so it must not be one click. It does NOT
             touch scheduled messages: they are kept, and are simply
             undeliverable until he links again. -->
        <div v-if="confirmingUnlink" class="manager-alert-unlink-confirm mwn-danger">
          <p class="mwn-sub">{{ t('managerAlertUnlinkConfirm') }}</p>
          <b-button
            variant="danger"
            size="sm"
            :disabled="unlinking"
            @click="unlinkAccountLink"
          >{{ t('managerAlertUnlinkConfirmYes') }}</b-button>
          <b-button variant="link" size="sm" @click="confirmingUnlink = false">
            {{ t('managerAlertUnlinkCancel') }}
          </b-button>
        </div>

        <!-- He replied STOP. Linked, known, and undeliverable: nothing
             auto-disables on this path, so without saying it the popup would
             show him linked while every send is refused. Both halves matter
             -- that it happened, and that any message to the business number
             resumes it. -->
        <p v-if="myLink.opted_out"
           class="mwn-sub manager-alert-opted-out mwn-notice">
          {{ t('managerAlertOptedOut') }} {{ t('managerAlertOptedOutResume') }}
        </p>

        <p v-if="error" class="mwn-error">{{ error }}</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { translationMixin } from '@/mixins/translationMixin';
import whatsappAccountLinkApi from '@/api/whatsappAccountLink';

export default {
  name: 'MyWhatsappNumber',
  mixins: [translationMixin],
  props: ["value"],
  data() {
    return {
      // ONE state object, because there is only one person here: the caller.
      // (The ride page used to hold two -- the ride's billed manager and the
      // caller -- and they raced. An account-level popup has no ride, so the
      // second one does not exist.)
      myLink: { linked: false, phone_masked: '', opted_out: false,
                link_code: '', link_url: '' },
      error: '',
      checking: false,
      minting: false,
      unlinking: false,
      // Unlink is destructive, account-wide and immediate, so it is two
      // clicks: this is the second one being armed.
      confirmingUnlink: false,
      // Replacing a number that already works. Kept separate from "not
      // linked" so the old number keeps showing as live until the new code
      // actually lands.
      changingNumber: false,
      phoneBeforeChange: '',
    };
  },
  computed: {
    linkMessageText() {
      // Decoded back out of link_url's ?text=, for the same reason the
      // number is: the backend owns this wording, and a second copy here
      // would drift from it the first time anyone reworded one side.
      //
      // The encoding is access.query_value, which is NOT standard percent
      // encoding -- it maps space to '+', escapes a short list of reserved
      // characters, and deliberately leaves Hebrew RAW so the text stays
      // readable in the URL. So '+' must become a space BEFORE decoding, or
      // a literal plus (sent as %2B) would come back as a space.
      const url = this.myLink && this.myLink.link_url;
      if ( !url ) return '';
      const marker = url.indexOf( '?text=' );
      if ( marker === -1 ) return '';
      const encoded = url.slice( marker + 6 );
      try {
        return decodeURIComponent( encoded.replace( /\+/g, ' ' ) );
      } catch ( error ) {
        // A malformed escape should not take the copy button down with it;
        // the caller falls back to the bare code.
        return '';
      }
    },

    systemWhatsappNumber() {
      // Read out of link_url (https://wa.me/<number>?text=...) rather than
      // configured here: it is then BY CONSTRUCTION the same number the
      // button sends to. A second copy in the frontend could drift, and a
      // manual code sent to the wrong number fails silently -- he would be
      // left waiting for a confirmation that can never arrive.
      const url = this.myLink && this.myLink.link_url;
      if ( !url ) return '';
      const match = /wa\.me\/(\d+)/.exec( url );
      return match ? '+' + match[1] : '';
    },

    ...mapGetters(['apptoken']),
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value(newVal) {
      if (newVal === true) {
        this.resetLocalState();
        this.loadAccountLink();
      }
    },
  },
  methods: {
    async copyLinkCode() {
      // Copies the WHOLE message, not just the code: what lands on the
      // clipboard is byte-for-byte what the button would have put in his
      // compose box, so the manual path and the tap path send the same
      // thing. The label before the code is load-bearing -- join_code's
      // parser needs it -- so copying the code alone would hand him
      // something that only works if he happens to retype the label too.
      //
      // Same shape as copyWhatsappInvite in EditRideAccessKey: clipboard
      // access is refused on an insecure origin and in some browsers, and a
      // copy button that silently does nothing is worse than none -- the
      // code stays on screen to be typed either way.
      try {
        await navigator.clipboard.writeText(
          this.linkMessageText || this.myLink.link_code );
        this.$bvToast.toast( this.t( 'managerAlertCopied' ),
          { variant: 'success', noCloseButton: true } );
      } catch ( error ) {
        this.$bvToast.toast( this.t( 'managerAlertCopyFailed' ),
          { variant: 'danger', noCloseButton: true } );
      }
    },

    resetLocalState() {
      this.error = '';
      this.confirmingUnlink = false;
      this.changingNumber = false;
      this.phoneBeforeChange = '';
      this.myLink = { ...this.myLink, link_code: '', link_url: '' };
    },
    async loadAccountLink( { fromRecheck = false } = {} ) {
      try {
        const response = await whatsappAccountLinkApi.getLink( this.apptoken );
        this.myLink = { ...this.myLink, ...response.data };
      } catch ( error ) {
        // Do NOT reset to "not linked". The endpoint answers 200 with
        // linked:false for an account that never minted, so an exception here
        // means the answer is UNKNOWN -- a 500, an expired session, a dropped
        // connection. Claiming "not linked" would offer to mint a new code to
        // a manager who IS linked, and on the re-check path it would wipe the
        // code and wa.me link he is about to send, then offer to mint a new
        // one and invalidate the code already in his compose box. Keeping the
        // last known state is the honest answer.
        if ( fromRecheck ) {
          this.error = this.t( 'managerAlertError' );
        }
      }
    },
    async recheckAccountLink() {
      this.error = '';
      this.checking = true;
      try {
        await this.loadAccountLink( { fromRecheck: true } );
      } finally {
        this.checking = false;
      }
      // Leave the replace flow only once the answer actually changed: while
      // replacing, `linked` stays true throughout (the old link is live until
      // the new code is sent), so `linked` alone would throw him out of the
      // flow on his first re-check and strand the code he is holding.
      if ( this.changingNumber ) {
        if ( this.myLink.phone_masked && this.myLink.phone_masked !== this.phoneBeforeChange ) {
          this.changingNumber = false;
          this.phoneBeforeChange = '';
          this.myLink = { ...this.myLink, link_code: '', link_url: '' };
        }
        return;
      }
      if ( this.myLink.linked ) {
        this.myLink = { ...this.myLink, link_code: '', link_url: '' };
      }
    },
    async mintAccountLink() {
      this.error = '';
      this.minting = true;
      try {
        const response = await whatsappAccountLinkApi.createLink(
          this.apptoken, this.isHebrew ? 'he' : 'en' );
        this.myLink = { ...this.myLink, ...response.data };
      } catch ( error ) {
        this.error = this.t( 'managerAlertError' );
      } finally {
        this.minting = false;
      }
    },
    startChangeNumber() {
      // Remember what is live now: the re-check below compares against it,
      // because that is the only way to tell "the new number arrived" from
      // "nothing has happened yet" while both report linked:true.
      this.phoneBeforeChange = this.myLink.phone_masked;
      this.error = '';
      this.confirmingUnlink = false;
      this.changingNumber = true;
      this.myLink = { ...this.myLink, link_code: '', link_url: '' };
      this.mintAccountLink();
    },
    cancelChangeNumber() {
      // Backing out does NOT unlink: the number that was working before is
      // still the one the server holds until a fresh code is actually sent.
      this.changingNumber = false;
      this.phoneBeforeChange = '';
      this.error = '';
      this.myLink = { ...this.myLink, link_code: '', link_url: '' };
    },
    async unlinkAccountLink() {
      // Reached only from the second click of the confirm. DELETE
      // /api/whatsapp/account_link is self-scoped: it acts on the CALLER's
      // account and deactivates the LINK and nothing else -- his scheduled
      // messages stay exactly as they are, undeliverable only until he links
      // again.
      this.error = '';
      this.unlinking = true;
      try {
        await whatsappAccountLinkApi.deleteLink( this.apptoken );
      } catch ( error ) {
        this.error = this.t( 'managerAlertError' );
        return;
      } finally {
        this.unlinking = false;
      }
      this.confirmingUnlink = false;
      this.changingNumber = false;
      this.phoneBeforeChange = '';
      this.myLink = { ...this.myLink, linked: false, phone_masked: '',
        opted_out: false, link_code: '', link_url: '' };
      await this.loadAccountLink();
    },
  },
};
</script>

<style scoped>
/* All sizes in px: html{font-size:10px} in this app makes rem misleading --
   the same reason OrganizerMessageModal.vue restyles what Bootstrap draws.
   Nothing here uses a physical left/right either: the panel is flipped by
   :dir on the modal, so every row is flex + gap and every edge that must
   follow the text direction is a logical property. */

.my-whatsapp-number {
  font-size: 15px;
  line-height: 1.6;
  color: #1f2d27;
  padding: 4px 6px 2px;
}

/* --- header ------------------------------------------------------------ */
.mwn-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 14px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e6ece9;
}

.mwn-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border-radius: 50%;
  background: #25D366;
  color: #fff;
}

.mwn-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
}

.mwn-explain {
  margin: 0 0 20px;
  font-size: 15px;
  color: #4a5a53;
}

/* --- the primary action ------------------------------------------------ */
.mwn-action {
  margin: 0 0 6px;
}

.mwn-primary {
  min-width: 220px;
  font-size: 17px;
  font-weight: 600;
  padding: 10px 26px;
  border-radius: 8px;
}

.mwn-sub {
  margin: 10px 0 0;
  font-size: 14px;
  color: #5a6a63;
  /* The opted-out line is two sentences joined by a blank line in the
     translation strings, the same idiom used elsewhere in the app. */
  white-space: pre-line;
}

.mwn-quiet {
  padding-inline-start: 0;
  font-size: 14px;
}

/* --- the manual path ---------------------------------------------------- */
.mwn-manual {
  margin-top: 22px;
}

/* A centred "or" rule. The lines are drawn with flex pseudo-elements rather
   than a background gradient so they size themselves in either direction. */
.mwn-or {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  color: #7b8a83;
  font-size: 13px;
}
.mwn-or::before,
.mwn-or::after {
  content: '';
  flex: 1 1 auto;
  height: 1px;
  background: #e6ece9;
}

.mwn-copy {
  /* Pushed to the far edge of the row, whichever edge that is. */
  margin-inline-start: auto;
  font-size: 13px;
}

.mwn-system-number {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}
.mwn-system-number code {
  direction: ltr;
  unicode-bidi: embed;
  font-size: 15px;
  font-weight: 600;
  color: #11301f;
  background: none;
  padding: 0;
}

/* --- the code, which is the thing he reads off the screen -------------- */
.mwn-code {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 16px 0 6px;
  padding: 14px 16px;
  border: 1px dashed #bcd9c7;
  border-radius: 10px;
  background: #f6fbf8;
}

.mwn-code-label {
  font-size: 13px;
  color: #5a6a63;
}

/* Latin text inside a page that may be RTL. Without an explicit direction
   the characters reorder and he cannot read his own code back. */
.mwn-code code {
  direction: ltr;
  unicode-bidi: embed;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #11301f;
  background: none;
  padding: 0;
}

/* --- linked: a status card, not a sentence ----------------------------- */
.mwn-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 18px;
  border: 1px solid #cdebd9;
  border-radius: 12px;
  background: #f3fbf6;
}

.mwn-status-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.mwn-tick {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 50%;
  background: #25D366;
  color: #fff;
}

.mwn-status-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.mwn-status-label {
  font-size: 13px;
  color: #5a6a63;
}

.mwn-phone {
  direction: ltr;
  unicode-bidi: embed;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #11301f;
  background: none;
  padding: 0;
}

.mwn-status-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.mwn-danger-link {
  color: #b02a37;
  font-size: 14px;
}

/* --- the two-click confirm, set apart because it acts immediately ------ */
.manager-alert-unlink-confirm {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.mwn-danger {
  margin-top: 14px;
  padding: 14px 16px;
  border: 1px solid #f0c9ce;
  border-radius: 10px;
  background: #fdf5f6;
}

.mwn-danger .mwn-sub {
  margin: 0;
  flex: 1 1 100%;
  color: #7a2a31;
}

/* --- notices ----------------------------------------------------------- */
.mwn-notice {
  margin-top: 14px;
  padding: 12px 14px;
  border-inline-start: 3px solid #e0a800;
  border-radius: 8px;
  background: #fffbf0;
  color: #5c4a12;
}

.mwn-error {
  margin-top: 14px;
  padding: 12px 14px;
  border-inline-start: 3px solid #b02a37;
  border-radius: 8px;
  background: #fdf3f4;
  font-size: 14px;
  color: #b02a37;
}

/* --- phone width -------------------------------------------------------- */
@media (max-width: 575px) {
  .mwn-title { font-size: 19px; }
  .mwn-primary { width: 100%; min-width: 0; }
  .mwn-code code { font-size: 18px; letter-spacing: 2px; }
  .mwn-status { align-items: flex-start; }
}
</style>
